import { format, isThisYear } from "date-fns";

export const isNullOrUndefined = (value: any): boolean => {
  return value === null || value === undefined;
};

export const getFirstInitial = (name: string): string => {
  return name.trim()[0].toUpperCase();
};

export const getInitials = (name: string) => {
  const nameParts = name.split(" ");
  const initials = nameParts
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
  return initials.toUpperCase();
};

export const transformToSentenceCase = (input: string) => {
  const result = input
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/(^\w|\s\w)/g, (match) => match.toUpperCase());

  return result;
};

export const camelCaseToSentenceCase = (str: string) => {
  const result = str.replace(/([A-Z])/g, " $1").toLowerCase();
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const convertUnderscoreToSentenceCase = (str = "") => {
  const words = str.toLowerCase().split("_");

  const capitalizedWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1)
  );

  return capitalizedWords.join(" ");
};

export const convertToTitleCase = (str: string) => {
  const exceptions = ["a"]; // List of words to not capitalize
  return str
    .split(" ")
    .map((word, index) => {
      // Always capitalize the first word
      if (index === 0 || !exceptions.includes(word.toLowerCase())) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word; // Keep the exception word as is
    })
    .join(" ");
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const formatAddress = (address: any) => {
  if (!address) return "";
  return `${address?.addressLine1}, ${address?.city}, ${address?.state} ${address?.postalCode}`;
};

export const formatPhoneNumber = (phone: string, excludeCountryCode: boolean = false) => {
  if (!phone) return "";

  const cleaned = phone.replace(/\D/g, "");

  // If excludeCountryCode is true, strip country code
  if (excludeCountryCode) {
    const usPhone = cleaned.length === 11 && cleaned.startsWith("1")
      ? cleaned.slice(1)
      : cleaned.length === 10
      ? cleaned
      : cleaned.slice(-10);
    return `(${usPhone.slice(0, 3)}) ${usPhone.slice(3, 6)}-${usPhone.slice(6)}`;
  }

  // Otherwise, include country code if present
  if (cleaned.length > 10) {
    const countryCodeLength = cleaned.length - 10;
    return `+${cleaned.slice(0, countryCodeLength)} (${cleaned.slice(
      countryCodeLength,
      countryCodeLength + 3
    )}) ${cleaned.slice(
      countryCodeLength + 3,
      countryCodeLength + 6
    )}-${cleaned.slice(countryCodeLength + 6)}`;
  }

  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

export const parsePhoneNumber = (phone: string) => {
  if (!phone) return "";

  const cleaned = phone.replace(/\D/g, "");

  return cleaned.length === 10 ? `+1${cleaned}` : `+${cleaned}`;
};

export const formatDateTime = (date: string | Date, isSlashFormat = false) => {
  if (isThisYear(date) && !isSlashFormat) {
    return format(date, "MMM d 'at' h:mmaaa");
  } else {
    return format(
      date,
      isSlashFormat ? "MM/dd/yyyy 'at' h:mmaaa" : "MMM d, yyyy 'at' h:mmaaa"
    );
  }
};

export const formatDate = (
  dateString: string,
  alwaysShowYear = false
): string => {
  const date = new Date(dateString);

  if (!alwaysShowYear && isThisYear(date)) {
    return format(date, "MMM d");
  }
  return format(date, "MMM d, yyyy");
};

// To format time as '4d', '2h', '5m', etc.
export const timeAgo = (dateTime: string) => {
  const now = new Date();
  const past = new Date(dateTime);
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  const secondsInMinute = 60;
  const secondsInHour = 3600;
  const secondsInDay = 86400;
  const secondsInWeek = 604800;

  if (diffInSeconds < secondsInMinute) {
    return `${diffInSeconds}s`;
  } else if (diffInSeconds < secondsInHour) {
    return `${Math.floor(diffInSeconds / secondsInMinute)}m`;
  } else if (diffInSeconds < secondsInDay) {
    return `${Math.floor(diffInSeconds / secondsInHour)}h`;
  } else if (diffInSeconds < secondsInWeek) {
    return `${Math.floor(diffInSeconds / secondsInDay)}d`;
  } else {
    return `${Math.floor(diffInSeconds / secondsInWeek)}w`;
  }
};

export const isObjectEmpty = (obj: object) => {
  return Object.keys(obj).length === 0;
};

export const toTitleCase = (str: string) =>
  str
    ?.replace(/_/g, " ")
    .split(" ")
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");

// utilities/calendarLinks.ts
export const generateCalendarLinks = (event: any) => {
  const { name, description, startDateTime, endDateTime } = event;

  // Can't generate calendar links without dates
  if (!startDateTime || !endDateTime) {
    return null;
  }

  // Format dates to UTC strings
  const start = new Date(startDateTime).toISOString();
  const end = new Date(endDateTime).toISOString();

  // Google Calendar link
  const googleLink = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
    name
  )}&dates=${start.replace(/-|:|\.\d+/g, "")}/${end.replace(
    /-|:|\.\d+/g,
    ""
  )}&details=${encodeURIComponent(description)}`;

  // Outlook Calendar link
  const outlookLink = `https://outlook.live.com/owa/?path=/calendar/action/compose&subject=${encodeURIComponent(
    name
  )}&body=${encodeURIComponent(description)}&startdt=${start}&enddt=${end}`;

  // iCal link (download .ics file)
  const icsData = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
SUMMARY:${name}
DESCRIPTION:${description}
DTSTART:${start.replace(/-|:|\.\d+/g, "")}
DTEND:${end.replace(/-|:|\.\d+/g, "")}
LOCATION:${location || ""}
END:VEVENT
END:VCALENDAR`;

  const icsLink = `data:text/calendar;charset=utf8,${encodeURIComponent(
    icsData
  )}`;

  return {
    google: googleLink,
    outlook: outlookLink,
    ical: icsLink,
  };
};

export const getDocumentModeUrlParam = (
  document: any,
  isWorkflowTemplatesRoute = false
) => {
  /// templates don't have a status
  const isTemplate = !document.status && isWorkflowTemplatesRoute;
  const basePath = `/document${isWorkflowTemplatesRoute ? "-templates" : "s"}/${document.id}`;

  // COMPLETE documents are always view-only
  if (document.status === "COMPLETE") {
    return `${basePath}/view`;
  }

  // READY_FOR_SIGNING goes to sign mode
  if (document.status === "READY_FOR_SIGNING") {
    return `${basePath}/sign`;
  }

  // PENDING_REVIEW goes to view mode
  if (document.status === "PENDING_REVIEW") {
    return `${basePath}/view`;
  }

  // VIEW_ONLY access (e.g., checked out by another team) goes to view
  if (document.viewAccess === "VIEW_ONLY") {
    return `${basePath}/view`;
  }

  // DRAFT and PUBLISHED with edit access go to edit mode
  if (document.status === "DRAFT" || document.status === "PUBLISHED" || isTemplate) {
    return `${basePath}/edit`;
  }

  return "";
};

export const getDocumentModeText = (document: any) => {
  if (
    document.status === "DRAFT" ||
    document.status === "COMPLETE" ||
    document.status === "PENDING_REVIEW" ||
    (document.status === "PUBLISHED" && document.viewAccess === "VIEW_ONLY")
  ) {
    return "View";
  } else if (document.status === "PUBLISHED") {
    return "Edit";
  } else if (document.status === "READY_FOR_SIGNING") {
    return "Sign";
  } else {
    return "";
  }
};

export const cn = (...inputs: any[]) => {
  return inputs.filter(Boolean).join(" ");
};

// Upload Configuration
export const UPLOAD_CONFIG = {
  acceptedFormats: "PDF",
  maxSize: "50 MB",
  header: {
    title: "Upload Tenant Application",
    subtitle: "Upload your own PDF or try one of our examples",
  },
  security: {
    title: "Secure & Private",
    description:
      "PDFs are processed securely in our AI cloud and never stored or shared.",
    privacyLink: "#",
  },
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};
